import React, {useState, useEffect, useRef, useMemo} from 'react';
import { useSearchParams } from 'react-router-dom';
import ProtectedRoute from '../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Avatar } from 'primereact/avatar';
import { Toast } from 'primereact/toast';
import performanceService from '../../services/data/performance.service';
import { Button } from 'primereact/button';
import NameInitials from '../../helpers/nameInitials';
import { Sidebar } from 'primereact/sidebar';
import { useFormik } from 'formik';
import moment from "moment";
import 'moment/locale/pt-br'
import SkeletonMatrix from './skeletonMatrix';
import UsePerms from "../../context/userAccess";
import useCompanyData from '../../context/company';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { Dialog } from 'primereact/dialog';
import companyService from '../../services/data/company.service';
import employeeService from '../../services/data/employee.service';
import MatrixPerformanceSource, { getMatrixCalibrationLabel } from '../../components/performance/matrixPerformanceSource';


function paramId(value) {
    if (!value) return '';
    const n = Number(value);
    return Number.isNaN(n) ? value : n;
}


export default function GeneralMatrix() {

    const VerifyPerms = UsePerms()
    const {rootUser} = useCompanyData()
    const [searchParams, setSearchParams] = useSearchParams()

    const [pageLoad] = useState(false)
    const toast = useRef(null);
    const [itemsList, setItemsList] = useState([]);
    const [empty, setEmpty] = useState(false)

    const [selection, setSelection] = useState();
    const [showSidebar, setShowSidebar] = useState(false);
    const [_clear] = useState(false);
    const [loading, setLoading] = useState(false);

    const [_filterLoad] = useState(false);
    const [subCompaniesList, setSubCompaniesList] = useState([]) // lista de subCompanies
    const [subcompany, setSubcompany] = useState(() => paramId(searchParams.get('subcompany')))

    const [internalJobTitlesList, setInternalJobTitlesList] = useState([]) // lista de cargos
    const [internalJobTitle, setInternalJobTitle] = useState(() => paramId(searchParams.get('job')))

    const [departmentsList, setDepartmentsList] = useState([]) // lista de departamentos
    const [department, setDepartment] = useState(() => paramId(searchParams.get('department')))
    const [filtersApplied, setFiltersApplied] = useState(() => Boolean(
        searchParams.get('subcompany') || searchParams.get('job') || searchParams.get('department') || searchParams.get('employee')
    ))

    const [employeeSearch, setEmployeeSearch] = useState(() => searchParams.get('employee_name') || '')
    const [employeeId, setEmployeeId] = useState(() => paramId(searchParams.get('employee')))
    const [employeeSuggestions, setEmployeeSuggestions] = useState([])
    const [employeeRawResults, setEmployeeRawResults] = useState([])

    const [showOverflowModal, setShowOverflowModal] = useState(false)
    const [overflowItems, setOverflowItems] = useState([])
    const [overflowPage, setOverflowPage] = useState(0)
    const [overflowBoxName, setOverflowBoxName] = useState('')
    const OVERFLOW_PAGE_SIZE = 10


    useEffect(() => {
        listSubCompanies()
        listInternalJobTitles()
        listDepartments()

        const urlFilters = {
            subcompany: paramId(searchParams.get('subcompany')),
            internalJobTitle: paramId(searchParams.get('job')),
            department: paramId(searchParams.get('department')),
            employeeId: paramId(searchParams.get('employee')),
        }

        if (urlFilters.subcompany || urlFilters.internalJobTitle || urlFilters.department || urlFilters.employeeId) {
            if (urlFilters.employeeId && !searchParams.get('employee_name')) {
                employeeService.getEmployee(urlFilters.employeeId).then((emp) => {
                    setEmployeeSearch(emp.user || emp.name || '')
                }).catch(() => {})
            }
            setLoading(true)
            listMatrix(false, urlFilters)
        }
    }, [])

    function listSubCompanies() {

        const getItem = ''

        companyService.autoCompleteSubCompany(getItem).then((response) => {
        
            let res = response.map((item) => {
                return {
                    title: item.title,
                    value: item.id
                }
            })
            setSubCompaniesList(res)

        })
        .catch((error) => {
            console.log(error.response.data)
        });

    }

    function listInternalJobTitles() {

        const getItem = ''

        companyService.autoCompleteInternalJobTitles(getItem).then((response) => {
            let res = response.map((item) => {
                return {
                    title: item.title,
                    value: item.id
                }
            })
            setInternalJobTitlesList(res)
        }).catch((error) => {
            console.log(error.response.data)
        })
    }

    function listDepartments() {

        const getItem = ''

        companyService.autoCompleteDepartment(getItem).then((response) => {
            let res = response.map((item) => {
                return {
                    title: item.title,
                    value: item.id
                }
            })
            setDepartmentsList(res)
        }).catch((error) => {
            console.log(error.response.data)
        })
    }

    function searchEmployee(event) {
        if (!event.query || event.query.length < 2) {
            setEmployeeSuggestions([])
            return
        }
        employeeService.autoCompleteEmployee(event.query).then((response) => {
            setEmployeeRawResults(response)
            setEmployeeSuggestions(response.map((item) => item.user))
        }).catch(() => setEmployeeSuggestions([]))
    }

    function onSelectEmployee(e) {
        const match = employeeRawResults.find((item) => item.user === e.value)
        if (match) {
            setEmployeeId(match.id)
            setEmployeeSearch(match.user)
        }
    }

    function clearEmployee() {
        setEmployeeSearch('')
        setEmployeeId('')
        setEmployeeSuggestions([])
    }

    function openOverflowModal(items, boxName) {
        setOverflowItems(items)
        setOverflowBoxName(boxName)
        setOverflowPage(0)
        setShowOverflowModal(true)
    }



    function syncFiltersToUrl(filters) {
        const params = {}
        if (filters.subcompany) params.subcompany = filters.subcompany
        if (filters.internalJobTitle) params.job = filters.internalJobTitle
        if (filters.department) params.department = filters.department
        if (filters.employeeId) {
            params.employee = filters.employeeId
            if (filters.employeeSearch) params.employee_name = filters.employeeSearch
        }
        setSearchParams(params, { replace: true })
    }

    const load = () => {
        setLoading(true);
        setFiltersApplied(true);
        syncFiltersToUrl({
            subcompany,
            internalJobTitle,
            department,
            employeeId,
            employeeSearch,
        });
        listMatrix()
    };

    function listMatrix(clearFilters = false, customFilters = null) {
        // Se não é para limpar filtros, verifica se pelo menos um filtro foi aplicado
        if (!clearFilters && !subcompany && !internalJobTitle && !department && !employeeId) {
            toast.current.show({ 
                severity: 'warn', 
                summary: 'Atenção', 
                detail: 'Aplique pelo menos um filtro antes de visualizar a matriz', 
                life: 4000 
            });
            setLoading(false);
            return;
        }

        // Limpa dados existentes e mostra skeleton
        setItemsList([])
        setEmpty(false)

        let data = {
            company_id: localStorage.getItem("companyId"),
        }

        // Usa filtros customizados se fornecidos, senão usa os estados
        const filtersToUse = customFilters || {
            subcompany: subcompany,
            internalJobTitle: internalJobTitle,
            department: department,
            employeeId: employeeId,
        }

        // Adiciona filtros se foram selecionados
        if (filtersToUse.subcompany) {
            data.subcompany_id = filtersToUse.subcompany
        }
        if (filtersToUse.internalJobTitle) {
            data.internal_job_title_id = filtersToUse.internalJobTitle
        }
        if (filtersToUse.department) {
            data.department_id = filtersToUse.department
        }
        if (filtersToUse.employeeId) {
            data.employee_id = filtersToUse.employeeId
        }

        performanceService.generalPerformanceMatrixGet(data).then((response) => {
            
            if (response.length > 0) {
                setItemsList(response)
                setEmpty(false)
            } else {
                setEmpty(true)
            }
            
        }).catch((error) => {
            console.log(error.response)
            toast.current.show({ 
                severity: 'error', 
                summary: 'Erro', 
                detail: 'Erro ao carregar matriz de performance', 
                life: 4000 
            });
        }).finally(() => {
            setLoading(false)
        });
    }

    function generateMatrix() {
        // Limpa dados existentes e mostra skeleton
        setItemsList([])
        setEmpty(false)

        let data = {
            company_id: localStorage.getItem("companyId"),
        }

        // Adiciona filtros se foram selecionados
        if (subcompany) {
            data.subcompany_id = subcompany
        }
        if (internalJobTitle) {
            data.internal_job_title_id = internalJobTitle
        }
        if (department) {
            data.department_id = department
        }

        performanceService.createPerformanceMatrix(data).then((response) => {   
            
            if (response.length > 0) {
                setItemsList(response)
                setEmpty(false)
            } else {
                setEmpty(true)
            }
            
        }).catch(() => {
            toast.current.show({ 
                severity: 'error', 
                summary: 'Erro', 
                detail: 'Erro ao gerar matriz de performance', 
                life: 4000 
            });
        }).finally(() => {
            setLoading(false)
        });
    }

    const box1 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 1 && item.score.box[1] === 1)
            return res
        }

    }, [itemsList])

    const box2 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 1 && item.score.box[1] === 2)
            return res
        }

    }, [itemsList])

    const box3 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 1 && item.score.box[1] === 3)
            return res
        }

    }, [itemsList])

    const box4 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 2 && item.score.box[1] === 1)
            return res
        }

    }, [itemsList])

    const box5 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 2 && item.score.box[1] === 2)
            return res
        }

    }, [itemsList])

    const box6 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 2 && item.score.box[1] === 3)
            return res
        }

    }, [itemsList])

    const box7 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 3 && item.score.box[1] === 1)
            return res
        }

    }, [itemsList])

    const box8 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 3 && item.score.box[1] === 2)
            return res
        }

    }, [itemsList])

    const box9 = useMemo(() => {

        if (itemsList) {

            let res = itemsList.filter((item) => item.score.box[0] === 3 && item.score.box[1] === 3)
            return res
        }

    }, [itemsList])

    const currentBox = useMemo(() => {

        if (selection) {

            if (selection.score.box[0] === 1 && selection.score.box[1] === 1) {
                return 'Detratores'
            }
            
            if (selection.score.box[0] === 1 && selection.score.box[1] === 2) {
                return 'Questionáveis'
            }
            
            if (selection.score.box[0] === 1 && selection.score.box[1] === 3) {
                return 'Latentes'
            }
            
            if (selection.score.box[0] === 2 && selection.score.box[1] === 1) {
                return 'Eficazes'
            }
            
            if (selection.score.box[0] === 2 && selection.score.box[1] === 2) {
                return 'Transitórios'
            }
            
            if (selection.score.box[0] === 2 && selection.score.box[1] === 3) {
                return 'Emergentes'
            }

            if (selection.score.box[0] === 3 && selection.score.box[1] === 1) {
                return 'Comprometidos'
            }

            if (selection.score.box[0] === 3 && selection.score.box[1] === 2) {
                return 'Emergentes'
            }
            
            if (selection.score.box[0] === 3 && selection.score.box[1] === 3) {
                return 'Estrelas'
            }
        }

    }, [selection])

    function truncateStr(str) {
        return str.length > 3 ? str.substring(0, 1): str;
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            box: '',
            reason: '',
        },
        onSubmit: values => {

            let data = {
                company_id: localStorage.getItem("companyId"),
                manual: true,
                assessment: selection.assessment,
                employee: selection.employee.id,
                box: values.box,
                related_matrix: selection.id,
                reason: values.reason
            }

            performanceService.calibratePerformanceMatrix(data).then(() => {    
                toast.current.show({ severity: 'success', summary: 'Sucesso', detail: `Processo enviado com sucesso`, life: 5000 });
                setLoading(true)
                // Recarrega a matriz com os filtros aplicados
                if (filtersApplied) {
                    listMatrix(false, {
                        subcompany: subcompany,
                        internalJobTitle: internalJobTitle,
                        department: department,
                        employeeId: employeeId,
                    })
                } else {
                    // Se não há filtros aplicados, gera matriz completa
                    generateMatrix()
                }
            setShowSidebar(false)
            setSelection('')
            formik.resetForm()

            }).catch((error) => {
                toast.current.show({ severity: 'warn', summary: 'Ops', detail: error.response.data, life: 4000 });
            });
        },
    });

    
    return (
        <ProtectedRoute>
            
            <Toast ref={toast} position="top-right" />

            <div className="container-fluid">
                <div className='row flex-nowrap'>
                    <div className='col text-bg-green p-3'>
                        <a href='/painel/' className="fs-6 fw-bold ps-5 text-decoration-none text-white">
                            <FontAwesomeIcon className='' icon={solid('arrow-left')} /> Matriz 9Box
                        </a>
                    </div>
                </div>
                <div className='my-4 mx-5 py-3 rounded-top' style={{minHeight: '500px'}}>
                    <div className='bg-white rounded'>

                        {
                            pageLoad || loading ?
                            <SkeletonMatrix />
                            :
                            <>
                            <div className='row mt-3'>
                                <div className='col-3'>
                                    <p className='fw-bold mt-3'>Filtros</p>
                                    <form className='bg-light p-3 rounded'>
                                        <div className="col mb-3">
                                            <label className="form-label">Organização</label>
                                            <div className='p-inputgroup'>
                                                <Dropdown filter placeholder="Selecionar" value={subcompany} optionLabel="title" optionValue="value" options={subCompaniesList} onChange={(e) => {setSubcompany(e.value);} } className="rounded" />
                                            </div>
                                        </div>
                                        <div className="col mb-3">
                                            <label className="form-label">Cargos</label>
                                            <div className='p-inputgroup'>
                                                <Dropdown filter placeholder="Selecionar" value={internalJobTitle} optionLabel="title" optionValue="value" options={internalJobTitlesList} onChange={(e) => {setInternalJobTitle(e.value);} } className="rounded" />
                                            </div>
                                        </div>
                                        <div className="col mb-3">
                                            <label className="form-label">Departamento</label>
                                            <div className='p-inputgroup'>
                                                <Dropdown filter placeholder="Selecionar" value={department} optionLabel="title" optionValue="value" options={departmentsList} onChange={(e) => {setDepartment(e.value);} } className="rounded" />
                                            </div>
                                        </div>
                                        <div className="col mb-3">
                                            <label className="form-label">Colaborador</label>
                                            <div className='p-inputgroup'>
                                                <AutoComplete
                                                    value={employeeSearch}
                                                    suggestions={employeeSuggestions}
                                                    completeMethod={searchEmployee}
                                                    onChange={(e) => { setEmployeeSearch(e.value); if (typeof e.value !== 'string' || e.value === '') setEmployeeId('') }}
                                                    onSelect={onSelectEmployee}
                                                    placeholder="Buscar por nome..."
                                                    inputClassName="w-100 rounded"
                                                    className="w-100"
                                                />
                                            </div>
                                            {employeeId && (
                                                <div className="mt-1 d-flex align-items-center gap-1">
                                                    <span className="badge bg-success text-white px-2 py-1">
                                                        <FontAwesomeIcon icon={solid('user')} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                        {employeeSearch}
                                                    </span>
                                                    <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={clearEmployee} title="Remover">
                                                        <FontAwesomeIcon icon={solid('times')} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="btnMargin">
                                            <button className="btn btn-success btn-sm btn-rounded px-4" type="button" onClick={load}>
                                                Filtrar
                                            </button>
                                            {
                                                filtersApplied ?
                                                <span role='button' className='fw-bold float-end mt-2' onClick={() => {
                                                    setSubcompany('')
                                                    setInternalJobTitle('')
                                                    setDepartment('')
                                                    clearEmployee()
                                                    setFiltersApplied(false)
                                                    setItemsList([])
                                                    setEmpty(false)
                                                    setSearchParams({}, { replace: true })
                                                }}>Limpar</span>
                                                :null
                                            }
                                            
                                        </div>
                                    </form>
                                </div>
                                <div className='col-9'>

                                    {
                                        empty ?
                                        <>
                                        <div className='text-center'>
                                            <p className='alert'>Nenhum dado encontrado para filtro aplicado.</p>
                                            <div className="btnMargin d-none">
                                                <Button rounded label="Gerar Matriz" type='submit' icon="pi pi-check" loading={loading} onClick={load} />
                                            </div>
                                        </div>
                                        </>
                                        : itemsList.length === 0 && !pageLoad && !loading ?
                                        <>
                                        <div className='text-center pt-5'>
                                            <p className='alert alert-warning'>Aplique filtros para visualizar a matriz de performance.</p>
                                        </div>
                                        </>
                                        : null
                                    }

                                    <div className='boxs'>

                                        <div className='w-100 d-flex'>
                                            <div className='verticalTxtBox'>
                                                <span className='text-center'>Acima da Expectativa</span>
                                            </div>
                                            
                                            <div className="box p-2 boxBlue">
                                                <p className='text-center'>Comprometidos</p>
                                                <div id='box7' className='d-flex flex-wrap justify-content-center'>
                                                    {
                                                        box7 && box7.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box7 && box7.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box7, 'Comprometidos')}>
                                                            <Avatar className="shadow"  label={truncateStr(`+${box7.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                            <div className="box p-2 boxGreenLight">
                                                <p className='text-center'>Emergentes</p>
                                                <div id='box8' className='d-flex flex-wrap justify-content-center'>
                                                    {
                                                        box8 && box8.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box8 && box8.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box8, 'Emergentes')}>
                                                            <Avatar className="shadow"  label={truncateStr(`+${box8.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                            <div className="box p-2 boxGreen">
                                                <p className='text-center'>Estrelas</p>
                                                <div id='box9' className='d-flex flex-wrap justify-content-center'>
                                                    {
                                                        box9 && box9.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box9 && box9.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box9, 'Estrelas')}>
                                                            <Avatar className="shadow"  label={truncateStr(`+${box9.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        <div className='w-100 d-flex'>
                                            <div className='verticalTxtBox'>
                                                <span>Dentro da Expectativa</span>
                                            </div>
                                            
                                            <div className="box p-2 boxYellow">
                                                <p className='text-center'>Eficazes</p>
                                                <div id='box4' className='d-flex flex-wrap justify-content-center'>
                                                    {
                                                        box4 && box4.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box4 && box4.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box4, 'Eficazes')}>
                                                        <Avatar className="shadow"  label={truncateStr(`+${box4.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                            <div className="box p-2 boxBlue">
                                                <p className='text-center'>Transitórios</p>
                                                <div id='box5' className='d-flex flex-wrap justify-content-center'>
                                                {
                                                        box5 && box5.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box5 && box5.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box5, 'Transitórios')}>
                                                            <Avatar className="shadow"  label={truncateStr(`+${box5.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                            <div className="box p-2 boxGreenLight">
                                                <p className='text-center'>Emergentes</p>
                                                <div id='box6' className='d-flex flex-wrap justify-content-center'>
                                                {
                                                        box6 && box6.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box6 && box6.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box6, 'Emergentes')}>
                                                        <Avatar className="shadow"  label={truncateStr(`+${box6.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        <div className='w-100 d-flex'>
                                            <div className='verticalTxtBox'>
                                                <span>Abaixo da Expectativa</span>
                                            </div>
                                            
                                            <div className="box p-2 boxRed">
                                                <p className='text-center'>Detratores</p>
                                                <div id='box1' className='d-flex flex-wrap justify-content-center'>
                                                    {
                                                        box1 && box1.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box1 && box1.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box1, 'Detratores')}>
                                                            <Avatar className="shadow"  label={truncateStr(`+${box1.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                            <div className="box p-2 boxYellow">
                                                <p className='text-center'>Questionáveis</p>
                                                <div id='box2' className='d-flex flex-wrap justify-content-center'>
                                                {
                                                        box2 && box2.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box2 && box2.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box2, 'Questionáveis')}>
                                                            <Avatar className="shadow"  label={truncateStr(`+${box2.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                            <div className="box p-2 boxBlue">
                                                <p className='text-center'>Latentes</p>
                                                <div id='box3' className='d-flex flex-wrap justify-content-center'>
                                                {
                                                        box3 && box3.slice(0,10).map((item) => 
                                                        <>
                                                        {
                                                            item.employee.user.photo ?
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                            :
                                                            <span role='button' className='mx-1' onClick={() => {setSelection(item); setShowSidebar(true)}}>
                                                                <Avatar className="shadow"  label={NameInitials( item.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                            </span>
                                                        }
                                                        </>
                                                        )
                                                    }
                                                    {
                                                        box3 && box3.length > 10 ?
                                                        <div className='m-2 border-top pt-3'>
                                                        <span role='button' onClick={() => openOverflowModal(box3, 'Latentes')}>
                                                            <Avatar className="shadow"  label={truncateStr(`+${box3.length}`)} style={{ backgroundColor: '#5ab1b8', color: '#ffffff', fontSize: '1rem' }} shape='circle' size='large' />
                                                        </span>
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        <div className='w-100 d-flex'>
                                            <div className='verticalTxtBox'>
                                                <span></span>
                                            </div>

                                            <div className='horizontalTxtBox'>
                                                <span>Baixo Potencial</span>
                                            </div>
                                            <div className='horizontalTxtBox'>
                                                <span>Médio Potencial</span>
                                            </div>
                                            <div className='horizontalTxtBox'>
                                                <span>Alto Potencial</span>
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                            </>
                        }
                        
                    </div>

                    <div className="card flex justify-content-center">
                        <Sidebar className="w-50" visible={showSidebar} position="right" onHide={() => {setShowSidebar(false); setSelection('')}}>
                        {
                                selection ?
                                <>
                                <div>
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td className='d-block align-content-start'>
                                                {
                                                    selection.employee.user.photo ?
                                                    <Avatar image={selection.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                    :
                                                    <Avatar label={NameInitials( selection.employee.user.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' size='large' />
                                                }
                                                </td>
                                                <td className='ps-2 lh-sm'>
                                                    <div className='fs-5 fw-bold'>
                                                        {selection.employee.user.name} {selection.employee.user.last_name}
                                                    </div>
                                                    <div className='fs-6 text-grey fw-normal'>{selection.jobposition.title}</div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <MatrixPerformanceSource selection={selection} />
                                <div className='mt-2'>
                                    <strong>Quadrante:</strong> {currentBox}
                                </div>
                                <div className='mt-2'>
                                    <strong>Calibração:</strong> {getMatrixCalibrationLabel(selection)}
                                </div>
                                <div className='mt-2'>
                                    <strong>Criado em:</strong> {moment(selection.created).format("DD-MMM-YYYY")}
                                </div>
                                {
                                    selection.reason ?
                                    <div className='mt-2'>
                                        <strong>Motivo:</strong> <span dangerouslySetInnerHTML={{ __html: selection.reason }}></span>
                                    </div>
                                    : null
                                }
                                {
                                    VerifyPerms(['add_performancematrix']) || rootUser ?
                                    <div className='mt-2 border-top pt-3'>
                                        {selection.calibration_enabled ? (
                                            <>
                                                <p className='fw-bold'>
                                                    Comitê de Calibração
                                                </p>
                                                <a
                                                    href={
                                                        selection.template_id
                                                            ? `/performance/calibration/${selection.template_id}/${selection.employee.id}/${selection.assessment}/`
                                                            : `/performance/calibration/0/${selection.employee.id}/${selection.assessment}/?review=${selection.assessment}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-success btn-sm rounded-pill px-4 py-2"
                                                >
                                                    Abrir Calibração Completa
                                                </a>
                                            </>
                                        ) : null}
                                    </div>
                                    : null
                                }
                                </>
                                :null
                            }
                        </Sidebar>
                    </div>
                </div>
            </div>

            <Dialog
                header={`${overflowBoxName} — todos os colaboradores`}
                visible={showOverflowModal}
                onHide={() => setShowOverflowModal(false)}
                style={{ width: '420px' }}
            >
                {overflowItems.slice(overflowPage * OVERFLOW_PAGE_SIZE, (overflowPage + 1) * OVERFLOW_PAGE_SIZE).map((item, idx) => (
                    <div
                        key={idx}
                        className='d-flex align-items-center p-2 border-bottom'
                        role='button'
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setSelection(item); setShowSidebar(true); setShowOverflowModal(false) }}
                    >
                        {item.employee.user.photo ?
                            <Avatar image={item.employee.user.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                            :
                            <Avatar label={NameInitials(item.employee.user.name)} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                        }
                        <div className='ms-2'>
                            <div className='fw-bold'>{item.employee.user.name} {item.employee.user.last_name}</div>
                            <div className='text-muted small'>{item.jobposition.title}</div>
                        </div>
                    </div>
                ))}
                {overflowItems.length > OVERFLOW_PAGE_SIZE && (
                    <div className='d-flex justify-content-between align-items-center mt-3'>
                        <button
                            className='btn btn-sm btn-outline-secondary'
                            disabled={overflowPage === 0}
                            onClick={() => setOverflowPage(p => p - 1)}
                        >
                            ← Anterior
                        </button>
                        <span className='small text-muted'>
                            {overflowPage + 1} / {Math.ceil(overflowItems.length / OVERFLOW_PAGE_SIZE)}
                        </span>
                        <button
                            className='btn btn-sm btn-outline-secondary'
                            disabled={overflowPage >= Math.ceil(overflowItems.length / OVERFLOW_PAGE_SIZE) - 1}
                            onClick={() => setOverflowPage(p => p + 1)}
                        >
                            Próxima →
                        </button>
                    </div>
                )}
            </Dialog>

        </ProtectedRoute>
    )
}